import { environment } from "@env/environment";
import { type IOnboardingCompletionSummaryResponse } from "@modules/onboarding/interfaces/IOnboardingCompletionSummaryResponse";
import { type IOnboardingProgressResponse } from "@modules/onboarding/interfaces/IOnboardingProgressResponse";
import { type IUpsertOnboardingProgressRequest } from "@modules/onboarding/interfaces/IUpsertOnboardingProgressRequest";
import {
  catchError,
  type Observable,
  shareReplay,
  tap,
  throwError,
} from "rxjs";

import { HttpClient } from "@angular/common/http";
import { inject, Service } from "@angular/core";

const PROGRESS_CACHE_MS = 30 * 1000;

@Service()
export class OnboardingProgressService {
  private readonly httpClient = inject(HttpClient);
  private readonly progressRequests = new Map<
    string,
    {
      expiresAt: number;
      request: Observable<IOnboardingProgressResponse>;
    }
  >();

  getProgress(tenant: string) {
    const cached = this.progressRequests.get(tenant);
    if (cached && cached.expiresAt > Date.now()) return cached.request;
    if (cached) this.progressRequests.delete(tenant);

    const request = this.httpClient
      .get<IOnboardingProgressResponse>(
        `${environment.API_URL}/api/onboarding-progress/${tenant}`
      )
      .pipe(
        catchError(error => {
          this.progressRequests.delete(tenant);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.progressRequests.set(tenant, {
      expiresAt: Date.now() + PROGRESS_CACHE_MS,
      request,
    });

    return request;
  }

  getCompletionSummary(tenant: string) {
    return this.httpClient.get<IOnboardingCompletionSummaryResponse>(
      `${environment.API_URL}/api/onboarding-progress/${tenant}/completion-summary`
    );
  }

  upsertProgress(tenant: string, request: IUpsertOnboardingProgressRequest) {
    return this.httpClient
      .post<IOnboardingProgressResponse>(
        `${environment.API_URL}/api/onboarding-progress/${tenant}`,
        request
      )
      .pipe(tap(() => this.progressRequests.delete(tenant)));
  }
}
